import React, { useState } from 'react';
import { View, Text } from 'react-native';
import i18n from 'i18n-js';

import { scale } from '@components/frame';
import { isPhone, SCREEN_PADDING_HORIZONTAL } from '@helpers';

import EditableLine from '@components/common/editableLine';
import LineButton from '@components/common/lineButton';
import TouchableElement from '@components/common/touchableElement';

import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';

import { DriversFoldersProps } from '@components/settings/v2/structure/connector/productionConnectorSetup/interfaces';
import { modalFieldBg } from '@v2/pages/common/constants/base.constants';

const EditFolders: React.FC<DriversFoldersProps> = ({ C, setMode, MODES, updateDataHistory, goBack, dataHistory }) => {
  const { editFolder, selectedFolder, folders = [] } = dataHistory;

  const [folder, setFolder] = useState<string>(selectedFolder?.folder || '');
  const [UNC, setUNC] = useState<string>(selectedFolder?.UNC || '');

  const saveDisabled = !folder || !UNC;

  return (
    <View style={{ paddingBottom: scale(20), paddingHorizontal: isPhone ? 0 : scale(15) }}>
      <View>
        <View
          style={{
            width: '100%',
            zIndex: 1000,
            height: scale(58),
          }}
        >
          <View style={{ position: 'absolute', zIndex: 1000, left: isPhone ? scale(SCREEN_PADDING_HORIZONTAL) : 0 }}>
            <TouchableElement
              onPress={() => {
                updateDataHistory({
                  selectedFolder: {},
                });
                goBack();
              }}
            >
              {({ boxStyle, pressHoverColor }) => (
                <View style={[boxStyle, { padding: scale(15), paddingLeft: 0, flexDirection: 'row' }]}>
                  <View style={{ marginTop: scale(2) }}>
                    <Svg icon="chevronLeft" width={scale(11)} height={scale(17)} color="Link / Light / Primary" />
                  </View>
                  <Text style={[{ color: C(pressHoverColor('System/Light/Blue')), marginLeft: scale(7) }, font.Body]}>{i18n.t('Back')}</Text>
                </View>
              )}
            </TouchableElement>
          </View>
          <View style={{ position: 'absolute', zIndex: 1000, right: isPhone ? scale(SCREEN_PADDING_HORIZONTAL) : 0 }}>
            <TouchableElement
              onPress={() => {
                if (saveDisabled) {
                  return;
                }
                const id = new Date().getTime();

                if (editFolder) {
                  const newFolders = folders.map((i) => {
                    if (i.id == selectedFolder?.id) {
                      return {
                        ...selectedFolder,
                        folder,
                        UNC,
                      };
                    }
                    return i;
                  });
                  updateDataHistory('folders', newFolders);
                } else {
                  updateDataHistory('folders', [...folders, { id, folder, UNC }]);
                }
                setMode(MODES.CONNECTOR_DETAILS);
              }}
            >
              {({ boxStyle, pressHoverColor }) => (
                <View style={[boxStyle, { padding: scale(15), paddingRight: 0 }]}>
                  <Text style={[{ color: C(pressHoverColor(!saveDisabled ? 'Link/Light/Primary' : 'Label/Light/Quinary')) }, font.Headline]}>
                    {i18n.t('Save')}
                  </Text>
                </View>
              )}
            </TouchableElement>
          </View>
        </View>
        <View style={{ width: '100%', position: 'absolute', zIndex: 100, top: scale(15) }}>
          <Text style={[{ color: C('Label/Light/Primary'), width: '100%', textAlign: 'center' }, font.Headline]}>{i18n.t('Drives & Folders')}</Text>
        </View>
      </View>
      <View>
        <View>
          <View style={{ paddingHorizontal: scale(15) }}>
            <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>{i18n.t('MAPPED DRIVE OR VIRTUAL FOLDER')}</Text>
          </View>
          <View style={{ marginTop: scale(5) }}>
            <EditableLine
              {...{
                item: {
                  value: folder,
                  key: 'folder',
                  placeholder: 'E.g. P: or \\oldserveroldfolder',
                  noArrow: true,
                  isLable: false,
                  onChange: (v: string) => setFolder(v),
                  small: false,
                },
                bg: modalFieldBg,
                index: 0,
                array: [{}],
                C,
                topBorder: false,
                bottomBorderBox: isPhone,
                topBorderBox: isPhone,
                roundTop: !isPhone,
                roundBottom: !isPhone,
              }}
            />
          </View>
        </View>
        <View>
          <View style={{ paddingHorizontal: scale(15), marginTop: scale(28) }}>
            <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>{i18n.t('UNC PATH')}</Text>
          </View>
          <View style={{ marginTop: scale(5) }}>
            <EditableLine
              {...{
                item: {
                  value: UNC,
                  key: 'UNC',
                  placeholder: 'E.g. \\newserver\\newfolder',
                  noArrow: true,
                  isLable: false,
                  onChange: (v: string) => setUNC(v),
                  small: false,
                },
                bg: modalFieldBg,
                index: 0,
                array: [{}],
                C,
                topBorder: false,
                bottomBorderBox: isPhone,
                topBorderBox: isPhone,
                roundTop: !isPhone,
                roundBottom: !isPhone,
              }}
            />
          </View>
          <View style={{ paddingHorizontal: scale(15), marginTop: scale(3) }}>
            <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary') }]}>
              <Text>
                <Text style={{ fontWeight: '700' }}>{i18n.t('Mapped Drive')}</Text>:{' '}
                {i18n.t(
                  'Smart Factory cannot see the network location (UNC path) of the mapped drive automatically. Therefore you need to enter it here.'
                )}
              </Text>
              {'\n\n'}
              <Text>
                <Text style={{ fontWeight: '700' }}>{i18n.t('Virtual Folder')}</Text>:{' '}
                {i18n.t(
                  'If a file cannot be accessed anymore in the old location (e.g. because of a modified folder name), Smart Factory will also search for this file in the virtual folder. A Virtual Folder is also called a Symbolic Link.'
                )}
              </Text>
            </Text>
          </View>
          {editFolder && (
            <View>
              <View style={{ marginTop: scale(30) }}>
                <LineButton
                  {...({
                    C,
                    onPress: () => {
                      const newFolders = folders.filter((i) => i.id !== selectedFolder?.id);
                      updateDataHistory('folders', newFolders);
                      setMode(MODES.CONNECTOR_DETAILS);
                    },
                    text: i18n.t('Remove'),
                    color: 'Label / Light / Error',
                    bg: modalFieldBg,
                  } as any)}
                />
              </View>
              <View style={{ paddingHorizontal: scale(15), marginTop: scale(3) }}>
                <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary') }]}>
                  {i18n.t('No folders or files will be deleted, only this mapping will be removed.')}
                </Text>
              </View>
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

export default EditFolders;
